import { useEffect, useMemo, useState } from 'react';
import { useStore } from '@/context/StoreContext';
import { navigate } from '@/hooks/useRoute';
import { ProductCard } from '@/components/products/ProductCard';
import { MEDIA } from '@/data/site';
import type { Product } from '@/types/product';
import './ProductPage.whatsapp.css';
import './ProductPage.moissanite-v4.css';
import './ProductPage.v11-3-quality.css';
import './ProductPage.v11-4-cleanup.css';
import './ProductPage.v11-5-experience.css';
import './ProductPage.v11-6-option2.css';
import './ProductPage.v11-9-premium-b.css';

import './ProductPage.v11-9-2-1-contained.css';
const money=(n:number)=>new Intl.NumberFormat('he-IL',{style:'currency',currency:'ILS',maximumFractionDigits:0}).format(n);


// LUVERA_PRODUCT_EXPERIENCE_V115
const LUVERA_RING_SIZES = [[47, 4, 14.97, 47], [48, 4.5, 15.3, 48], [49, 5, 15.6, 49], [50, 5.25, 15.9, 50], [51, 5.5, 16.24, 51], [52, 6, 16.5, 52.1], [53, 6.5, 16.8, 52.8], [54, 6.75, 17.2, 54], [55, 7, 17.5, 55.1], [56, 7.5, 17.8, 56], [57, 8, 18.1, 57], [58, 8.25, 18.4, 58], [59, 8.75, 18.8, 58.9], [60, 9, 19.1, 60], [61, 9.25, 19.4, 61], [62, 9.75, 19.7, 62.1], [63, 10.25, 20, 63], [64, 10.5, 20.3, 64], [65, 11, 20.7, 65], [66, 11.5, 21.02, 65.9], [67, 12, 21.34, 66.9]] as const;
const LUVERA_ANDROID_RING_SIZER='https://play.google.com/store/apps/details?id=ru.cherrydesign.ringsizer';
const LUVERA_IOS_RING_SIZER='https://apps.apple.com/il/app/ring-sizer-by-jason-withers/id795721582';
const PRODUCT_WHATSAPP='972556649480';

function openProductWhatsApp(
 p:Product,
 adjusted:number,
 options:{metal:string;purity:string;carat:string;size:string;length:string;engraving:string;ringLike:boolean;ringSizeSystem:string}
){
 const lines=[
  'שלום, אשמח לקבל פרטים על תכשיט המוסונייט הבא:',
  '',
  `💎 ${p.name}`,
  p.mielleySku?`מק״ט: ${p.mielleySku}`:'',
  p.categoryLabel?`קטגוריה: ${p.categoryLabel}`:'',
  `מחיר באתר: ${money(adjusted)}`,
  p.stone?`אבן: מוסונייט`:'',
  p.shape?`צורה: ${p.shape}`:'',
  options.carat?`גודל אבן נבחר: ${options.carat}`:(p.carat?`גודל אבן: ${p.carat}`:''),
  options.metal?`מתכת: ${options.metal}`:'',
  options.purity?`גימור: ${options.purity}`:'',
  options.ringLike&&options.size?`מידת טבעת (${options.ringSizeSystem||'EU'}): ${options.size}`:'',
  options.length?`אורך נבחר: ${options.length}`:'',
  options.engraving?`חריטה: ${options.engraving}`:'',
  '',
  '',
  `קישור למוצר: ${window.location.href}`
 ].filter(Boolean).join('\n');

 const url=`https://wa.me/${PRODUCT_WHATSAPP}?text=${encodeURIComponent(lines)}`;
 window.open(url,'_blank','noopener,noreferrer');
}

function normalizeSlug(value:string){
 let s=String(value||'').trim();
 // WooCommerce may return non-Latin slugs URL-encoded, while the browser route
 // is decoded by App.tsx. Decode safely (also tolerates a double-encoded slug).
 for(let i=0;i<2;i++){
  try{
   const decoded=decodeURIComponent(s);
   if(decoded===s)break;
   s=decoded;
  }catch{break}
 }
 try{s=s.normalize('NFC')}catch{}
 return s.replace(/^\/+|\/+$/g,'').toLowerCase();
}

function sharesCategory(a:Product,b:Product){
 const ak=new Set([a.category,...(a.categoryKeys||[])]);
 const bk=new Set([b.category,...(b.categoryKeys||[])]);
 return [...ak].some(k=>bk.has(k));
}

export function ProductPage({slug,previewProduct}:{slug:string;previewProduct?:Product}){
 const {products,loading,addToCart,toggleWishlist,wishlist,toggleCompare}=useStore();
 const normalizedRouteSlug=useMemo(()=>normalizeSlug(slug),[slug]);
 const p=useMemo(
  ()=>previewProduct||products.find(x=>normalizeSlug(x.slug)===normalizedRouteSlug),
  [products,normalizedRouteSlug,previewProduct]
 );

 const [image,setImage]=useState(0);
 const [metal,setMetal]=useState('כסף סטרלינג 925');
 const [purity,setPurity]=useState('Rhodium');
 const [carat,setCarat]=useState('1.00 CT');
 const [size,setSize]=useState('54');
 const [length,setLength]=useState('');
 const [engraving,setEngraving]=useState('');
 const [lv115Tab,setLv115Tab]=useState<'details'|'care'|'shipping'>('details');
 const [lv115SizeGuide,setLv115SizeGuide]=useState(false);

 useEffect(()=>{
  if(!lv115SizeGuide)return;
  const previous=document.body.style.overflow;
  document.body.style.overflow='hidden';
  const onKey=(e:KeyboardEvent)=>{if(e.key==='Escape')setLv115SizeGuide(false)};
  window.addEventListener('keydown',onKey);
  return ()=>{document.body.style.overflow=previous;window.removeEventListener('keydown',onKey)};
 },[lv115SizeGuide]);

 useEffect(()=>{
  if(!p)return;
  const availableSizes=p.ringSizes?.length?p.ringSizes:LUVERA_RING_SIZES.map(x=>String(x[0]));
  setSize(availableSizes[0]||'');
  setLength(p.lengthOptions?.[0]||'');
 },[p?.id]);

 if(loading&&!p)return <section className="section empty"><h1>טוען מוצר…</h1></section>;
 if(!p)return <section className="section empty"><h1>המוצר לא נמצא</h1><button className="btn gold" onClick={()=>navigate('/catalog')}>חזרה לקטלוג</button></section>;

 const adjusted=p.price;
 const related=products.filter(x=>x.id!==p.id&&x.status==='active'&&sharesCategory(x,p)).slice(0,4);
 const hiddenImages=new Set(p.hiddenImages||[]);
 const visibleGallery=(p.gallery||[]).filter(img=>img&&!hiddenImages.has(img));
 const gallery=visibleGallery.length?visibleGallery:[p.image].filter(img=>img&&!hiddenImages.has(img));
 const ringLike=['engagement','rings','wedding'].some(k=>p.category===k||(p.categoryKeys||[]).includes(k)) || /טבעת/.test(`${p.categoryLabel||''} ${p.name||''}`);
 const ringSizeSystem=p.ringSizeSystem||'EU';
 const availableRingSizes=p.ringSizes?.length?p.ringSizes:LUVERA_RING_SIZES.map(x=>String(x[0]));
 const availableLengths=p.lengthOptions||[];
 const descriptionText=/יהלום|יהלומי|מעבדה|זהב|14K|18K/i.test(p.description||'')
  ? `${p.name} בעיצוב נקי ומעודן, משובץ מוסונייט ומיועד למראה בהיר ויוקרתי על בסיס כסף סטרלינג 925 בגימור רודיום.`
  : (p.description||`${p.name} — תכשיט מוסונייט בעיצוב נקי, אלגנטי ועל־זמני.`);
 const cleanJewelryLabel=(value:string)=>String(value||'')
  .replace(/טבעות אירוסין/g,'טבעות מוסונייט')
  .replace(/טבעת אירוסין/g,'טבעת מוסונייט')
  .replace(/טבעות נישואין/g,'טבעות מוסונייט')
  .replace(/יהלומי מעבדה|יהלום מעבדה|יהלומים|יהלום/g,'מוסונייט')
  .replace(/\s*-\s*/g,' — ')
  .trim();
 const displayName=cleanJewelryLabel(p.name);
 const displayCategory=cleanJewelryLabel(p.categoryLabel);
 // LUVERA_OPTION2_V116
 // LUVERA_PREMIUM_B_V119
 // LUVERA_SINGLE_IMAGE_FIX_V1191
 return <>
  <nav className="breadcrumbs container mp4-breadcrumbs"><a href="/" onClick={e=>{e.preventDefault();navigate('/')}}>בית</a><span>›</span><a href={`/catalog?category=${p.category}`} onClick={e=>{e.preventDefault();navigate(`/catalog?category=${p.category}`)}}>{displayCategory}</a><span>›</span><strong>{displayName}</strong></nav>

  <section className="mp119-product">
   <div className="container mp119-layout">
    <div className={`mp119-gallery ${gallery.length>1?'has-thumbs':'single-image'}`}>
     {gallery.length>1&&
      <div className="mp119-thumbs" aria-label="גלריית תמונות">
       {gallery.slice(0,6).map((img,i)=>
        <button
         type="button"
         key={img+i}
         className={'mp119-thumb '+(i===image?'active':'')}
         onClick={()=>setImage(i)}
         aria-label={`תמונה ${i+1} של ${displayName}`}
        >
         <img src={img} alt=""/>
        </button>
       )}
      </div>
     }

     <div className="mp119-main">
      <span className="mp119-gallery-badge">MIELLEY · MOISSANITE · 925</span>
      <img src={gallery[image]||p.image} alt={displayName}/>
      <button
       type="button"
       className="mp119-zoom"
       aria-label="פתיחת תמונה בגודל מלא"
       onClick={()=>window.open(gallery[image]||p.image,'_blank')}
      >
       ↗
      </button>
     </div>
    </div>

    <aside className="mp119-summary">
     <div className="mp119-kicker">THE MIELLEY EDIT</div>
     <h1>{displayName}</h1>
     <small className="mp119-sku">{displayCategory} · {p.mielleySku}</small>

     <div className="mp119-quality" aria-label="מפרט עיקרי">
      <span>MOISSANITE</span>
      {p.carat&&<span>{p.carat}</span>}
      {p.color&&<span>{p.color}</span>}
      {p.clarity&&<span>{p.clarity}</span>}
      <span>925 SILVER</span>
     </div>

     <div className="mp119-price">
      <strong>{money(adjusted)}</strong>
      {p.oldPrice>p.price&&<del>{money(p.oldPrice)}</del>}
     </div>
     <small className="mp119-payments">או עד 12 תשלומים של {money(Math.ceil(adjusted/12))}</small>

     <p className="mp119-description">{descriptionText}</p>

     <div className="mp119-highlights">
      <div className="mp119-highlight">
       <b>Premium Moissanite</b>
       <small>{[p.carat,p.color,p.clarity].filter(Boolean).join(' · ')||'ברק ונוכחות מדויקת'}</small>
      </div>
      <div className="mp119-highlight">
       <b>925 Sterling Silver</b>
       <small>כסף סטרלינג בגימור Rhodium</small>
      </div>
      <div className="mp119-highlight">
       <b>משלוח מבוטח</b>
       <small>{p.deliveryDays||'לפי זמינות הדגם'}</small>
      </div>
     </div>

     <div className="mp119-options">
      {ringLike&&<div className="mp119-option">
       <div className="mp119-option-head">
        <strong>מידת טבעת · {ringSizeSystem}</strong>
        <button type="button" className="mp119-size-guide" onClick={()=>setLv115SizeGuide(true)}>מדריך מידות</button>
       </div>
       <select value={size} onChange={e=>setSize(e.target.value)} aria-label={`בחירת מידת טבעת ${ringSizeSystem}`}>
        {availableRingSizes.map(x=><option key={x} value={x}>{x}</option>)}
       </select>

       <button type="button" className="mp119-size-callout" onClick={()=>setLv115SizeGuide(true)}>
        <i aria-hidden="true">⌁</i>
        <span><b>לא בטוחה במידה שלך?</b><small>טבלת EU, קוטר, היקף ואפליקציות מדידה</small></span>
        <em aria-hidden="true">←</em>
       </button>
      </div>}

      {availableLengths.length>0&&<div className="mp119-option">
       <div className="mp119-option-head"><strong>אורך</strong><span>לפי זמינות הדגם</span></div>
       <select value={length} onChange={e=>setLength(e.target.value)} aria-label="בחירת אורך">
        {availableLengths.map(x=><option key={x} value={x}>{x}</option>)}
       </select>
      </div>}

      {p.engraving&&<div className="mp119-option">
       <div className="mp119-option-head"><strong>חריטה אישית</strong><span>אופציונלי</span></div>
       <input maxLength={24} value={engraving} onChange={e=>setEngraving(e.target.value)} placeholder="עד 24 תווים"/>
      </div>}
     </div>

     <div className="mp119-buy">
      <button className="mp119-add" disabled={Boolean(previewProduct)} onClick={()=>{if(!previewProduct)addToCart(p.id,adjusted,{metal,purity,carat:p.carat||carat,size,length,engraving})}}>
       {previewProduct?'Preview · הוספה לסל מושבתת':`הוספה לסל · ${money(adjusted)}`}
      </button>
      <button className="mp119-wa" disabled={Boolean(previewProduct)} onClick={()=>{if(!previewProduct)openProductWhatsApp(p,adjusted,{metal,purity,carat:p.carat||carat,size,length,engraving,ringLike,ringSizeSystem})}}>
       {previewProduct?'WhatsApp מושבת ב-Preview':'WhatsApp · ייעוץ אישי'}
      </button>
     </div>

     <div className="mp119-actions">
      <button disabled={Boolean(previewProduct)} onClick={()=>{if(!previewProduct)toggleWishlist(p.id)}}>{wishlist.includes(p.id)?'♥ במועדפים':'♡ מועדפים'}</button>
      <button disabled={Boolean(previewProduct)} onClick={()=>{if(!previewProduct)toggleCompare(p.id)}}>⇄ השוואה</button>
      <button onClick={()=>navigator.share?.({title:p.name,url:location.href})}>↗ שיתוף</button>
     </div>

     <div className="mp119-trust">
      <div><b>אריזה מוקפדת</b><small>מתאימה גם למתנה</small></div>
      <div><b>ליווי אישי</b><small>לפני ואחרי הרכישה</small></div>
      <div><b>מידע מלא</b><small>מפרט שקוף לכל דגם</small></div>
     </div>
    </aside>
   </div>
  </section>

  <section className="lv115-info" aria-label="מידע חשוב על המוצר">
   <div className="lv115-info-inner">
    <div className="lv115-tabs" role="tablist" aria-label="מידע על התכשיט">
     <button type="button" role="tab" aria-selected={lv115Tab==='details'} className={'lv115-tab '+(lv115Tab==='details'?'active':'')} onClick={()=>setLv115Tab('details')}>פרטי התכשיט</button>
     <button type="button" role="tab" aria-selected={lv115Tab==='care'} className={'lv115-tab '+(lv115Tab==='care'?'active':'')} onClick={()=>setLv115Tab('care')}>אחריות וטיפול</button>
     <button type="button" role="tab" aria-selected={lv115Tab==='shipping'} className={'lv115-tab '+(lv115Tab==='shipping'?'active':'')} onClick={()=>setLv115Tab('shipping')}>משלוחים והחזרות</button>
    </div>
    <div className="lv115-panel">
     {lv115Tab==='details'&&<>
      <div className="lv115-panel-head"><span>JEWELRY DETAILS</span><h3>הפרטים שעושים את ההבדל.</h3><p>כל הנתונים החשובים של הדגם מרוכזים במקום אחד — בצורה ברורה, נקייה וקלה להשוואה.</p></div>
      <div className="lv115-detail-grid">
       <div className="lv115-detail"><small>אבן</small><b>Moissanite</b></div>
       {p.carat&&<div className="lv115-detail"><small>גודל האבן</small><b>{p.carat}</b></div>}
       {p.color&&<div className="lv115-detail"><small>צבע</small><b>{p.color}</b></div>}
       {p.clarity&&<div className="lv115-detail"><small>ניקיון</small><b>{p.clarity}</b></div>}
       <div className="lv115-detail"><small>מתכת</small><b>925 Sterling Silver</b></div>
       <div className="lv115-detail"><small>גימור</small><b>Rhodium Finish</b></div>
       {p.mielleySku&&<div className="lv115-detail"><small>מק״ט</small><b>{p.mielleySku}</b></div>}
       <div className="lv115-detail"><small>אספקה</small><b>{p.deliveryDays||'לפי זמינות הדגם'}</b></div>
      </div>
     </>}
     {lv115Tab==='care'&&<>
      <div className="lv115-panel-head"><span>MIELLEY CARE</span><h3>לשמור על הברק, לאורך זמן.</h3><p>אנחנו רוצים שהתכשיט יישאר יפה ומדויק גם הרבה אחרי הרגע הראשון.</p></div>
      <ul className="lv115-copy-list">
       <li>האחריות והשירות ניתנים בהתאם למדיניות MIELLEY, תנאי השימוש ואופי המוצר.</li>
       <li>מומלץ להסיר את התכשיט לפני שחייה, מקלחת ממושכת או שימוש בחומרי ניקוי.</li>
       <li>יש להימנע ממגע ישיר וקבוע עם בושם, כלור וחומרים כימיים.</li>
       <li>לאחסון נכון, שמרי את התכשיט במקום יבש ובנפרד מתכשיטים אחרים.</li>
       <li>לניקוי שוטף מומלץ להשתמש במטלית רכה ונקייה ולהימנע מחומרים שוחקים.</li>
       <li>בכל שאלה על תחזוקה או שירות, צוות MIELLEY זמין לייעוץ אישי.</li>
      </ul>
      <button type="button" className="lv115-policy-link" onClick={()=>navigate('/contact')}>צריכה עזרה? דברי איתנו ←</button>
     </>}
     {lv115Tab==='shipping'&&<>
      <div className="lv115-panel-head"><span>DELIVERY & RETURNS</span><h3>משלוח מוקפד. בחירה רגועה.</h3><p>כל הזמנה נארזת בקפידה ונשלחת בהתאם לזמינות הדגם ולמדיניות האתר.</p></div>
      <ul className="lv115-copy-list">
       <li>זמן האספקה לדגם זה: <b>{p.deliveryDays||'לפי זמינות'}</b>.</li>
       <li>המשלוח יוצא באריזה מוקפדת ומתאימה לתכשיט.</li>
       <li>אפשרויות החלפה והחזרה חלות בהתאם למדיניות האתר, סוג הפריט והדין.</li>
       <li>במוצר שהותאם אישית, נחרט או יוצר במיוחד עשויים לחול תנאים שונים.</li>
      </ul>
      <button type="button" className="lv115-policy-link" onClick={()=>navigate('/shipping')}>למדיניות משלוחים והחזרות ←</button>
     </>}
    </div>
   </div>
  </section>

  <section className="mp4-details lv115-details">
   <div className="container mp4-details-layout">
    <div className="mp4-details-intro">
     <span>THE DETAILS</span>
     <h2>מפרט מלא.<br/>בלי סימני שאלה.</h2>
     <p>המפרט המלא של הדגם — סוג האבן, הגודל, המתכת, הגימור ופרטי האיכות כפי שנשמרו במוצר.</p>
     <div className="lv115-details-note">
      <div><small>MATERIAL</small><b>925 STERLING SILVER · RHODIUM</b></div>
      <div><small>STONE</small><b>{p.carat?('MOISSANITE · '+p.carat):'MOISSANITE'}</b></div>
     </div>
    </div>

    <aside className="mp4-specs">
     <span>JEWELRY SPECIFICATION</span>
     <h3>מפרט התכשיט</h3>
     <div className="mp4-spec-row"><span>אבן</span><b className="aqua">Moissanite</b></div>
     {p.shape&&<div className="mp4-spec-row"><span>צורה</span><b>{p.shape}</b></div>}
     {p.carat&&<div className="mp4-spec-row"><span>גודל האבן</span><b>{p.carat}</b></div>}
     <div className="mp4-spec-row"><span>מתכת</span><b>925 Sterling Silver</b></div>
     <div className="mp4-spec-row"><span>גימור</span><b>Rhodium Finish</b></div>
      {p.color&&<div className="mp4-spec-row"><span>צבע</span><b>{p.color}</b></div>}
      {p.clarity&&<div className="mp4-spec-row"><span>ניקיון</span><b>{p.clarity}</b></div>}
     <div className="mp4-spec-row"><span>מק״ט</span><b>{p.mielleySku}</b></div>
     <div className="mp4-spec-row"><span>זמן אספקה</span><b>{p.deliveryDays||'לפי זמינות'}</b></div>
    </aside>
   </div>
  </section>

  <section className="mp4-care">
   <div className="container mp4-care-layout">
    <div className="mp4-care-media"><img src={MEDIA.included} alt="אריזת תכשיט ושירות"/></div>
    <div className="mp4-care-copy">
     <span>PREMIUM CARE</span>
     <h2>תכשיט אונליין.<br/>חוויה של בוטיק.</h2>
     <ul>
      <li>אריזה מוקפדת שמתאימה גם למתנה</li>
      <li>משלוח מבוטח ומעקב אחר ההזמנה</li>
      <li>מדריך מידות ברור לפני הזמנת טבעת</li>
      <li>ייעוץ אישי ב־WhatsApp לפני הרכישה</li>
      <li>שירות וליווי גם לאחר קבלת התכשיט</li>
     </ul>
    </div>
   </div>
  </section>

  {related.length>0&&<section className="mp4-related">
   <div className="container">
    <div className="mp4-related-head"><div><span>YOU MAY ALSO LIKE</span><h2>אולי תאהבו גם</h2></div><a href="/catalog" onClick={e=>{e.preventDefault();navigate('/catalog')}}>לכל הקולקציה ←</a></div>
    <div className="product-grid">{related.map(x=><ProductCard key={x.id} product={x}/>)}</div>
   </div>
  </section>}

  <div className="mp4-mobile-buy">
   <div><span>{displayName}</span><b>{money(adjusted)}</b></div>
   <button className="mp4-mobile-wa" disabled={Boolean(previewProduct)} onClick={()=>{if(!previewProduct)openProductWhatsApp(p,adjusted,{metal,purity,carat:p.carat||carat,size,length,engraving,ringLike,ringSizeSystem})}}>WhatsApp</button>
   <button disabled={Boolean(previewProduct)} onClick={()=>{if(!previewProduct)addToCart(p.id,adjusted,{metal,purity,carat:p.carat||carat,size,length,engraving})}}>{previewProduct?'Preview':'הוספה לסל'}</button>
  </div>
 
  {lv115SizeGuide&&<div className="lv115-modal" role="presentation" onMouseDown={e=>{if(e.target===e.currentTarget)setLv115SizeGuide(false)}}>
   <section className="lv115-modal-card" role="dialog" aria-modal="true" aria-labelledby="lv115-size-title">
    <button type="button" className="lv115-modal-close" aria-label="סגירת מדריך מידות" onClick={()=>setLv115SizeGuide(false)}>×</button>
    <header className="lv115-modal-hero">
     <span className="lv115-modal-kicker">MIELLEY SIZE GUIDE</span>
     <h2 id="lv115-size-title">למצוא את המידה הנכונה.</h2>
     <p>מדריך קצר שיעזור לך לבחור את מידת הטבעת בנוחות ובביטחון. מומלץ למדוד כאשר היד בטמפרטורת גוף רגילה ולבדוק את האצבע שעליה תענדי את הטבעת בפועל.</p>
    </header>
    <div className="lv115-modal-body">
     <section className="lv115-guide-section">
      <h3>לפני שמתחילים</h3>
      <div className="lv115-guide-tips">
       <div className="lv115-guide-tip"><b>אותה אצבע, אותה יד</b><span>מידות יכולות להשתנות בין יד ימין לשמאל — מדדי את האצבע הרלוונטית.</span></div>
       <div className="lv115-guide-tip"><b>טמפרטורה רגילה</b><span>לא מומלץ למדוד כשהיד קרה מאוד או חמה ונפוחה.</span></div>
       <div className="lv115-guide-tip"><b>בין שתי מידות?</b><span>אם התוצאה נמצאת בין שתי מידות, בדרך כלל עדיף לבחור במידה הגדולה הקרובה.</span></div>
      </div>
     </section>
     <section className="lv115-guide-section">
      <h3>אפשרות 1 — מדידה עם אפליקציה</h3>
      <p>אם יש לך טבעת שמתאימה לאצבע הרצויה, אפשר להניח אותה על המסך ולהתאים את העיגול לקוטר הפנימי שלה. באתר MIELLEY אנחנו עובדים לפי מידות אירופאיות (EU).</p>
      <div className="lv115-apps">
       <a className="lv115-app" href={LUVERA_ANDROID_RING_SIZER} target="_blank" rel="noreferrer">Android · הורדת Ring Sizer ↗</a>
       <a className="lv115-app" href={LUVERA_IOS_RING_SIZER} target="_blank" rel="noreferrer">iPhone · הורדת Ring Sizer ↗</a>
      </div>
     </section>
     <section className="lv115-guide-section">
      <h3>אפשרות 2 — נייר וסרגל</h3>
      <p>אפשר למדוד גם בבית בעזרת רצועת נייר דקה וסרגל. המדידה צריכה להיות נוחה — לא לוחצת ולא רופפת מדי.</p>
      <div className="lv115-paper-steps">
       <div className="lv115-paper-step"><b>גזרי רצועה</b><span>הכיני רצועת נייר צרה, מספריים וסרגל.</span></div>
       <div className="lv115-paper-step"><b>לפפי סביב האצבע</b><span>העבירי את הנייר סביב החלק הרחב ביותר וסמני את נקודת המפגש.</span></div>
       <div className="lv115-paper-step"><b>מדדי במ״מ</b><span>פתחי את הרצועה ומדדי את האורך במילימטרים.</span></div>
       <div className="lv115-paper-step"><b>השווי לטבלה</b><span>מצאי את ההיקף הקרוב ביותר ובחרי את מידת EU המתאימה.</span></div>
      </div>
     </section>
     <section className="lv115-guide-section">
      <h3>טבלת מידות טבעות</h3>
      <p>המידה המופיעה באתר היא מידת EU. ניתן להשוות גם למידת USA, לקוטר פנימי של טבעת קיימת או להיקף האצבע.</p>
      <div className="lv115-table-wrap">
       <table className="lv115-size-table">
        <thead><tr><th>מידת אירופה EU</th><th>מידת ארה״ב USA</th><th>קוטר טבעת (מ״מ)</th><th>היקף האצבע (מ״מ)</th></tr></thead>
        <tbody>
         {LUVERA_RING_SIZES.map(([eu,usa,diameter,circumference])=><tr key={eu} className={eu===54||eu===56?'standard':''}>
          <td>{eu}{(eu===54||eu===56)&&<small> · סטנדרט</small>}</td>
          <td className="ltr">{usa}</td><td className="ltr">{diameter}</td><td className="ltr">{circumference}</td>
         </tr>)}
        </tbody>
       </table>
      </div>
     </section>
     <div className="lv115-modal-help">
      <div><b>עדיין מתלבטת לגבי המידה?</b><span>שלחי לנו הודעה ונשמח לעזור לפני ההזמנה.</span></div>
      <a href="https://wa.me/972556649480" target="_blank" rel="noreferrer">ייעוץ ב־WhatsApp ←</a>
     </div>
    </div>
   </section>
  </div>}

  </>;
}
